import { Injectable } from '@angular/core';
import {
  AppBackgroundOpacities,
  AppBackgroundPositions,
  AppBackgroundSizes,
  AppBorderRadiuses,
  AppBoxShadows,
  AppColorPalette,
  AppColorShades,
  AppColSpans,
  AppContainerAlign,
  AppContainerPalette,
  AppTailwindDisplayType,
  AppDisplayTypes,
  AppFlexAlign,
  AppFlexDirection,
  AppFlexJustify,
  AppFlexWrap,
  AppFontSizes,
  AppFontWeights,
  AppGradientDirections,
  AppHeights,
  AppImagesFixedWidths,
  AppImageWidthsPercentage,
  AppMaxFixedWidths,
  AppSpacingSizes,
  AppTailwindResponsiveDevices,
  AppTailwindResponsivePrefixes,
  AppTextAlign,
  AppTextColors,
  AppTextItalic,
  AppTextTracking,
  AppTextTransforms,
  AppWidths,
} from '../types/types';
import {
  backgroundColorsInterface,
  backgroundOpacityInterface,
  bgColorsShadesInterface,
  bgPosInterface,
  borderRadiusInterface,
  colSpanInterface,
  colXGapInterface,
  colYGapInterface,
  displayInterface,
  flexInterface,
  gradientInterface,
  heightInterface,
  imgMaxWInterface,
  marginAlignInterface,
  marginInterface,
  maxWInterface,
  paddingsInterface,
  shadowInterface,
  textInterface,
  widthInterface,
} from './tailwind-service-types';

@Injectable({
  providedIn: 'root',
})
export class TailwindService {
  //                 Helper Functions                 //
  getResponsivePrefix(
    screenSize: AppTailwindResponsiveDevices = '',
  ): AppTailwindResponsivePrefixes {
    switch (screenSize) {
      case 'tablet':
        return 'md:';
      case 'desktop':
        return 'lg:';
      default:
        return '';
    }
  }

  //                 Mobile classes                   //

  //                   Typography
  getTextSpacingClass(
    letterSpacing?: AppTextTracking,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (letterSpacing) {
      /**
       * classes have to be written out for tailwind, so they appear in the css
       * */
      case 'unset':
        return '';
      case 'tighter':
        /**
         * tracking-tighter
         * md:tracking-tighter
         * lg:tracking-tighter
         * */
        return `${prefix}tracking-tighter`;
      case 'tight':
        /**
         * tracking-tight
         * md:tracking-tight
         * lg:tracking-tight
         * */
        return `${prefix}tracking-tight`;
      case 'normal':
        /**
         * tracking-normal
         * md:tracking-normal
         * lg:tracking-normal
         * */
        return `${prefix}tracking-normal`;
      case 'wide':
        /**
         * tracking-wide
         * md:tracking-wide
         * lg:tracking-wide
         * */
        return `${prefix}tracking-wide`;
      case 'wider':
        /**
         * tracking-wider
         * md:tracking-wider
         * lg:tracking-wider
         * */
        return `${prefix}tracking-wider`;
      case 'widest':
        /**
         * tracking-widest
         * md:tracking-widest
         * lg:tracking-widest
         * */
        return `${prefix}tracking-widest`;
      default:
        return '';
    }
  }

  getFontWeightClass(
    fontWeight?: AppFontWeights,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (fontWeight) {
      case 'unset':
        return '';
      case 'thin':
        /**
         * font-thin
         * md:font-thin
         * lg:font-thin
         * */
        return `${prefix}font-thin`;
      case 'extralight':
        /**
         * font-extralight
         * md:font-extralight
         * lg:font-extralight
         * */
        return `${prefix}font-extralight`;
      case 'light':
        /**
         * font-light
         * md:font-light
         * lg:font-light
         * */
        return `${prefix}font-light`;
      case 'normal':
        /**
         * font-normal
         * md:font-normal
         * lg:font-normal
         * */
        return `${prefix}font-normal`;
      case 'medium':
        /**
         * font-medium
         * md:font-medium
         * lg:font-medium
         * */
        return `${prefix}font-medium`;
      case 'semibold':
        /**
         * font-semibold
         * md:font-semibold
         * lg:font-semibold
         * */
        return `${prefix}font-semibold`;
      case 'bold':
        /**
         * font-bold
         * md:font-bold
         * lg:font-bold
         * */
        return `${prefix}font-bold`;
      case 'extrabold':
        /**
         * font-extrabold
         * md:font-extrabold
         * lg:font-extrabold
         * */
        return `${prefix}font-extrabold`;
      case 'black':
        /**
         * font-black
         * md:font-black
         * lg:font-black
         * */
        return `${prefix}font-black`;
      default:
        return '';
    }
  }

  getFontSizeClass(
    fontSize?: AppFontSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (fontSize) {
      case 'unset':
        return '';
      case 'xs':
        /**
         * text-xs
         * md:text-xs
         * lg:text-xs
         * */
        return `${prefix}text-xs`;
      case 'sm':
        /**
         * text-sm
         * md:text-sm
         * lg:text-sm
         * */
        return `${prefix}text-sm`;
      case 'base':
        /**
         * text-base
         * md:text-base
         * lg:text-base
         * */
        return `${prefix}text-base`;
      case 'lg':
        /**
         * text-lg
         * md:text-lg
         * lg:text-lg
         * */
        return `${prefix}text-lg`;
      case 'xl':
        /**
         * text-xl
         * md:text-xl
         * lg:text-xl
         * */
        return `${prefix}text-xl`;
      case '2xl':
        /**
         * text-2xl
         * md:text-2xl
         * lg:text-2xl
         * */
        return `${prefix}text-2xl`;
      case '3xl':
        /**
         * text-3xl
         * md:text-3xl
         * lg:text-3xl
         * */
        return `${prefix}text-3xl`;
      case '4xl':
        /**
         * text-4xl
         * md:text-4xl
         * lg:text-4xl
         * */
        return `${prefix}text-4xl`;
      case '5xl':
        /**
         * text-5xl
         * md:text-5xl
         * lg:text-5xl
         * */
        return `${prefix}text-5xl`;
      case '6xl':
        /**
         * text-6xl
         * md:text-6xl
         * lg:text-6xl
         * */
        return `${prefix}text-6xl`;
      case '7xl':
        /**
         * text-7xl
         * md:text-7xl
         * lg:text-7xl
         * */
        return `${prefix}text-7xl`;
      case '8xl':
        /**
         * text-8xl
         * md:text-8xl
         * lg:text-8xl
         * */
        return `${prefix}text-8xl`;
      case '9xl':
        /**
         * text-9xl
         * md:text-9xl
         * lg:text-9xl
         * */
        return `${prefix}text-9xl`;
      default:
        return '';
    }
  }

  getTextAlignClass(
    textAlign?: AppTextAlign,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (textAlign) {
      case 'unset':
        return '';
      case 'left':
        /**
         * text-left
         * md:text-left
         * lg:text-left
         * */
        return `${prefix}text-left`;
      case 'center':
        /**
         * text-center
         * md:text-center
         * lg:text-center
         * */
        return `${prefix}text-center`;
      case 'right':
        /**
         * text-right
         * md:text-right
         * lg:text-right
         * */
        return `${prefix}text-right`;
      default:
        return '';
    }
  }

  getTextColorClass(
    color?: AppTextColors,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (color) {
      case 'unset':
        return '';
      case 'primary':
        /**
         * text-primary
         * md:text-primary
         * lg:text-primary
         * */
        return `${prefix}text-primary`;
      case 'secondary':
        /**
         * text-secondary
         * md:text-secondary
         * lg:text-secondary
         * */
        return `${prefix}text-secondary`;
      case 'accent':
        /**
         * text-accent
         * md:text-accent
         * lg:text-accent
         * */
        return `${prefix}text-accent`;
      case 'grey':
        /**
         * text-grey
         * md:text-grey
         * lg:text-grey
         * */
        return `${prefix}text-grey`;
      case 'white':
        /**
         * text-white
         * md:text-white
         * lg:text-white
         * */
        return `${prefix}text-white`;
      case 'inherit':
        /**
         * text-inherit
         * md:text-inherit
         * lg:text-inherit
         * */
        return `${prefix}text-inherit`;
      default:
        return '';
    }
  }

  getItalicClass(
    italic?: AppTextItalic,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (italic) {
      case 'unset':
        return '';
      case 'italic':
        /**
         * italic
         * md:italic
         * lg:italic
         * */
        return `${prefix}italic`;
      case 'normal':
        /**
         * not-italic
         * md:not-italic
         * lg:not-italic
         * */
        return `${prefix}not-italic`;
      default:
        return '';
    }
  }

  getTextTransformClass(
    textTransform?: AppTextTransforms,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (textTransform) {
      case 'unset':
        return '';
      case 'uppercase':
        /**
         * uppercase
         * md:uppercase
         * lg:uppercase
         */
        return `${prefix}uppercase`;
      case 'lowercase':
        /**
         * lowercase
         * md:lowercase
         * lg:lowercase
         */
        return `${prefix}lowercase`;
      case 'capitalize':
        /**
         * capitalize
         * md:capitalize
         * lg:capitalize
         */
        return `${prefix}capitalize`;
      case 'normal-case':
        /**
         * normal-case
         * md:normal-case
         * lg:normal-case
         */
        return `${prefix}normal-case`;
      default:
        return '';
    }
  }

  //   Spacing
  //     Spacing Y
  getMarginYClass(
    margin?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (margin) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * my-0.5
         * md:my-0.5
         * lg:my-0.5
         */
        return `${prefix}my-0.5`;
      case 'xs':
        /**
         * my-1
         * md:my-1
         * lg:my-1
         */
        return `${prefix}my-1`;
      case 's':
        /**
         * my-2
         * md:my-2
         * lg:my-2
         */
        return `${prefix}my-2`;
      case 'md':
        /**
         * my-3
         * md:my-3
         * lg:my-3
         */
        return `${prefix}my-3`;
      case 'l':
        /**
         * my-4
         * md:my-4
         * lg:my-4
         */
        return `${prefix}my-4`;
      case 'xl':
        /**
         * my-5
         * md:my-5
         * lg:my-5
         */
        return `${prefix}my-5`;
      case '2xl':
        /**
         * my-6
         * md:my-6
         * lg:my-6
         */
        return `${prefix}my-6`;
      case '3xl':
        /**
         * my-8
         * md:my-8
         * lg:my-8
         */
        return `${prefix}my-8`;
      case '4xl':
        /**
         * my-12
         * md:my-12
         * lg:my-12
         */
        return `${prefix}my-12`;
      case '5xl':
        /**
         * my-16
         * md:my-16
         * lg:my-16
         */
        return `${prefix}my-16`;
      case '6xl':
        /**
         * my-20
         * md:my-20
         * lg:my-20
         */
        return `${prefix}my-20`;
      case '7xl':
        /**
         * my-24
         * md:my-24
         * lg:my-24
         */
        return `${prefix}my-24`;
      case '8xl':
        /**
         * my-32
         * md:my-32
         * lg:my-32
         */
        return `${prefix}my-32`;
      case '9xl':
        /**
         * my-40
         * md:my-40
         * lg:my-40
         */
        return `${prefix}my-40`;

      default:
        return '';
    }
  }

  getMarginBottomClass(
    margin?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (margin) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * mb-0.5
         * md:mb-0.5
         * lg:mb-0.5
         */
        return `${prefix}mb-0.5`;
      case 'xs':
        /**
         * mb-1
         * md:mb-1
         * lg:mb-1
         */
        return `${prefix}mb-1`;
      case 's':
        /**
         * mb-2
         * md:mb-2
         * lg:mb-2
         */
        return `${prefix}mb-2`;
      case 'md':
        /**
         * mb-3
         * md:mb-3
         * lg:mb-3
         */
        return `${prefix}mb-3`;
      case 'l':
        /**
         * mb-4
         * md:mb-4
         * lg:mb-4
         */
        return `${prefix}mb-4`;
      case 'xl':
        /**
         * mb-5
         * md:mb-5
         * lg:mb-5
         */
        return `${prefix}mb-5`;
      case '2xl':
        /**
         * mb-6
         * md:mb-6
         * lg:mb-6
         */
        return `${prefix}mb-6`;
      case '3xl':
        /**
         * mb-8
         * md:mb-8
         * lg:mb-8
         */
        return `${prefix}mb-8`;
      case '4xl':
        /**
         * mb-12
         * md:mb-12
         * lg:mb-12
         */
        return `${prefix}mb-12`;
      case '5xl':
        /**
         * mb-16
         * md:mb-16
         * lg:mb-16
         */
        return `${prefix}mb-16`;
      case '6xl':
        /**
         * mb-20
         * md:mb-20
         * lg:mb-20
         */
        return `${prefix}mb-20`;
      case '7xl':
        /**
         * mb-24
         * md:mb-24
         * lg:mb-24
         */
        return `${prefix}mb-24`;
      case '8xl':
        /**
         * mb-32
         * md:mb-32
         * lg:mb-32
         */
        return `${prefix}mb-32`;
      case '9xl':
        /**
         * mb-40
         * md:mb-40
         * lg:mb-40
         */
        return `${prefix}mb-40`;
      default:
        return '';
    }
  }

  getMarginTopClass(
    margin?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (margin) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * mt-0.5
         * md:mt-0.5
         * lg:mt-0.5
         */
        return `${prefix}mt-0.5`;
      case 'xs':
        /**
         * mt-1
         * md:mt-1
         * lg:mt-1
         */
        return `${prefix}mt-1`;
      case 's':
        /**
         * mt-2
         * md:mt-2
         * lg:mt-2
         */
        return `${prefix}mt-2`;
      case 'md':
        /**
         * mt-3
         * md:mt-3
         * lg:mt-3
         */
        return `${prefix}mt-3`;
      case 'l':
        /**
         * mt-4
         * md:mt-4
         * lg:mt-4
         */
        return `${prefix}mt-4`;
      case 'xl':
        /**
         * mt-5
         * md:mt-5
         * lg:mt-5
         */
        return `${prefix}mt-5`;
      case '2xl':
        /**
         * mt-6
         * md:mt-6
         * lg:mt-6
         */
        return `${prefix}mt-6`;
      case '3xl':
        /**
         * mt-8
         * md:mt-8
         * lg:mt-8
         */
        return `${prefix}mt-8`;
      case '4xl':
        /**
         * mt-12
         * md:mt-12
         * lg:mt-12
         */
        return `${prefix}mt-12`;
      case '5xl':
        /**
         * mt-16
         * md:mt-16
         * lg:mt-16
         */
        return `${prefix}mt-16`;
      case '6xl':
        /**
         * mt-20
         * md:mt-20
         * lg:mt-20
         */
        return `${prefix}mt-20`;
      case '7xl':
        /**
         * mt-24
         * md:mt-24
         * lg:mt-24
         */
        return `${prefix}mt-24`;
      case '8xl':
        /**
         * mt-32
         * md:mt-32
         * lg:mt-32
         */
        return `${prefix}mt-32`;
      case '9xl':
        /**
         * mt-40
         * md:mt-40
         * lg:mt-40
         */
        return `${prefix}mt-40`;
      default:
        return '';
    }
  }

  //     Spacing X
  getMarginXClass(
    margin?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (margin) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * mx-0.5
         * md:mx-0.5
         * lg:mx-0.5
         */
        return `${prefix}mx-0.5`;
      case 'xs':
        /**
         * mx-1
         * md:mx-1
         * lg:mx-1
         */
        return `${prefix}mx-1`;
      case 's':
        /**
         * mx-2
         * md:mx-2
         * lg:mx-2
         */
        return `${prefix}mx-2`;
      case 'md':
        /**
         * mx-3
         * md:mx-3
         * lg:mx-3
         */
        return `${prefix}mx-3`;
      case 'l':
        /**
         * mx-4
         * md:mx-4
         * lg:mx-4
         */
        return `${prefix}mx-4`;
      case 'xl':
        /**
         * mx-5
         * md:mx-5
         * lg:mx-5
         */
        return `${prefix}mx-5`;
      case '2xl':
        /**
         * mx-6
         * md:mx-6
         * lg:mx-6
         */
        return `${prefix}mx-6`;
      case '3xl':
        /**
         * mx-8
         * md:mx-8
         * lg:mx-8
         */
        return `${prefix}mx-8`;
      case '4xl':
        /**
         * mx-12
         * md:mx-12
         * lg:mx-12
         */
        return `${prefix}mx-12`;
      case '5xl':
        /**
         * mx-16
         * md:mx-16
         * lg:mx-16
         */
        return `${prefix}mx-16`;
      case '6xl':
        /**
         * mx-20
         * md:mx-20
         * lg:mx-20
         */
        return `${prefix}mx-20`;
      case '7xl':
        /**
         * mx-24
         * md:mx-24
         * lg:mx-24
         */
        return `${prefix}mx-24`;
      case '8xl':
        /**
         * mx-32
         * md:mx-32
         * lg:mx-32
         */
        return `${prefix}mx-32`;
      case '9xl':
        /**
         * mx-40
         * md:mx-40
         * lg:mx-40
         */
        return `${prefix}mx-40`;
      default:
        return '';
    }
  }

  getMarginLeftClass(
    margin?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (margin) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * ml-0.5
         * md:ml-0.5
         * lg:ml-0.5
         */
        return `${prefix}ml-0.5`;
      case 'xs':
        /**
         * ml-1
         * md:ml-1
         * lg:ml-1
         */
        return `${prefix}ml-1`;
      case 's':
        /**
         * ml-2
         * md:ml-2
         * lg:ml-2
         */
        return `${prefix}ml-2`;
      case 'md':
        /**
         * ml-3
         * md:ml-3
         * lg:ml-3
         */
        return `${prefix}ml-3`;
      case 'l':
        /**
         * ml-4
         * md:ml-4
         * lg:ml-4
         */
        return `${prefix}ml-4`;
      case 'xl':
        /**
         * ml-5
         * md:ml-5
         * lg:ml-5
         */
        return `${prefix}ml-5`;
      case '2xl':
        /**
         * ml-6
         * md:ml-6
         * lg:ml-6
         */
        return `${prefix}ml-6`;
      case '3xl':
        /**
         * ml-8
         * md:ml-8
         * lg:ml-8
         */
        return `${prefix}ml-8`;
      case '4xl':
        /**
         * ml-12
         * md:ml-12
         * lg:ml-12
         */
        return `${prefix}ml-12`;
      case '5xl':
        /**
         * ml-16
         * md:ml-16
         * lg:ml-16
         */
        return `${prefix}ml-16`;
      case '6xl':
        /**
         * ml-20
         * md:ml-20
         * lg:ml-20
         */
        return `${prefix}ml-20`;
      case '7xl':
        /**
         * ml-24
         * md:ml-24
         * lg:ml-24
         */
        return `${prefix}ml-24`;
      case '8xl':
        /**
         * ml-32
         * md:ml-32
         * lg:ml-32
         */
        return `${prefix}ml-32`;
      case '9xl':
        /**
         * ml-40
         * md:ml-40
         * lg:ml-40
         */
        return `${prefix}ml-40`;
      default:
        return '';
    }
  }

  getMarginRightClass(
    margin?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (margin) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * mr-0.5
         * md:mr-0.5
         * lg:mr-0.5
         */
        return `${prefix}mr-0.5`;
      case 'xs':
        /**
         * mr-1
         * md:mr-1
         * lg:mr-1
         */
        return `${prefix}mr-1`;
      case 's':
        /**
         * mr-2
         * md:mr-2
         * lg:mr-2
         */
        return `${prefix}mr-2`;
      case 'md':
        /**
         * mr-3
         * md:mr-3
         * lg:mr-3
         */
        return `${prefix}mr-3`;
      case 'l':
        /**
         * mr-4
         * md:mr-4
         * lg:mr-4
         */
        return `${prefix}mr-4`;
      case 'xl':
        /**
         * mr-5
         * md:mr-5
         * lg:mr-5
         */
        return `${prefix}mr-5`;
      case '2xl':
        /**
         * mr-6
         * md:mr-6
         * lg:mr-6
         */
        return `${prefix}mr-6`;
      case '3xl':
        /**
         * mr-8
         * md:mr-8
         * lg:mr-8
         */
        return `${prefix}mr-8`;
      case '4xl':
        /**
         * mr-12
         * md:mr-12
         * lg:mr-12
         */
        return `${prefix}mr-12`;
      case '5xl':
        /**
         * mr-16
         * md:mr-16
         * lg:mr-16
         */
        return `${prefix}mr-16`;
      case '6xl':
        /**
         * mr-20
         * md:mr-20
         * lg:mr-20
         */
        return `${prefix}mr-20`;
      case '7xl':
        /**
         * mr-24
         * md:mr-24
         * lg:mr-24
         */
        return `${prefix}mr-24`;
      case '8xl':
        /**
         * mr-32
         * md:mr-32
         * lg:mr-32
         */
        return `${prefix}mr-32`;
      case '9xl':
        /**
         * mr-40
         * md:mr-40
         * lg:mr-40
         */
        return `${prefix}mr-40`;
      default:
        return '';
    }
  }

  //   Padding
  //     Padding Y
  getPaddingYClass(
    padding?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (padding) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * py-0.5
         * md:py-0.5
         * lg:py-0.5
         */
        return `${prefix}py-0.5`;
      case 'xs':
        /**
         * py-1
         * md:py-1
         * lg:py-1
         */
        return `${prefix}py-1`;
      case 's':
        /**
         * py-2
         * md:py-2
         * lg:py-2
         */
        return `${prefix}py-2`;
      case 'md':
        /**
         * py-3
         * md:py-3
         * lg:py-3
         */
        return `${prefix}py-3`;
      case 'l':
        /**
         * py-4
         * md:py-4
         * lg:py-4
         */
        return `${prefix}py-4`;
      case 'xl':
        /**
         * py-5
         * md:py-5
         * lg:py-5
         */
        return `${prefix}py-5`;
      case '2xl':
        /**
         * py-6
         * md:py-6
         * lg:py-6
         */
        return `${prefix}py-6`;
      case '3xl':
        /**
         * py-8
         * md:py-8
         * lg:py-8
         */
        return `${prefix}py-8`;
      case '4xl':
        /**
         * py-12
         * md:py-12
         * lg:py-12
         */
        return `${prefix}py-12`;
      case '5xl':
        /**
         * py-16
         * md:py-16
         * lg:py-16
         */
        return `${prefix}py-16`;
      case '6xl':
        /**
         * py-20
         * md:py-20
         * lg:py-20
         */
        return `${prefix}py-20`;
      case '7xl':
        /**
         * py-24
         * md:py-24
         * lg:py-24
         */
        return `${prefix}py-24`;
      case '8xl':
        /**
         * py-32
         * md:py-32
         * lg:py-32
         */
        return `${prefix}py-32`;
      case '9xl':
        /**
         * py-40
         * md:py-40
         * lg:py-40
         */
        return `${prefix}py-40`;
      case 'none':
        /**
         * py-0
         * md:py-0
         * lg:py-0
         */
        return `${prefix}py-0`;
      default:
        return '';
    }
  }

  getPaddingBottomClass(
    padding?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (padding) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * pb-0.5
         * md:pb-0.5
         * lg:pb-0.5
         */
        return `${prefix}pb-0.5`;
      case 'xs':
        /**
         * pb-1
         * md:pb-1
         * lg:pb-1
         */
        return `${prefix}pb-1`;
      case 's':
        /**
         * pb-2
         * md:pb-2
         * lg:pb-2
         */
        return `${prefix}pb-2`;
      case 'md':
        /**
         * pb-3
         * md:pb-3
         * lg:pb-3
         */
        return `${prefix}pb-3`;
      case 'l':
        /**
         * pb-4
         * md:pb-4
         * lg:pb-4
         */
        return `${prefix}pb-4`;
      case 'xl':
        /**
         * pb-5
         * md:pb-5
         * lg:pb-5
         */
        return `${prefix}pb-5`;
      case '2xl':
        /**
         * pb-6
         * md:pb-6
         * lg:pb-6
         */
        return `${prefix}pb-6`;
      case '3xl':
        /**
         * pb-8
         * md:pb-8
         * lg:pb-8
         */
        return `${prefix}pb-8`;
      case '4xl':
        /**
         * pb-12
         * md:pb-12
         * lg:pb-12
         */
        return `${prefix}pb-12`;
      case '5xl':
        /**
         * pb-16
         * md:pb-16
         * lg:pb-16
         */
        return `${prefix}pb-16`;
      case '6xl':
        /**
         * pb-20
         * md:pb-20
         * lg:pb-20
         */
        return `${prefix}pb-20`;
      case '7xl':
        /**
         * pb-24
         * md:pb-24
         * lg:pb-24
         */
        return `${prefix}pb-24`;
      case '8xl':
        /**
         * pb-32
         * md:pb-32
         * lg:pb-32
         */
        return `${prefix}pb-32`;
      case '9xl':
        /**
         * pb-40
         * md:pb-40
         * lg:pb-40
         */
        return `${prefix}pb-40`;
      default:
        return '';
    }
  }

  getPaddingTopClass(
    padding?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (padding) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * pt-0.5
         * md:pt-0.5
         * lg:pt-0.5
         */
        return `${prefix}pt-0.5`;
      case 'xs':
        /**
         * pt-1
         * md:pt-1
         * lg:pt-1
         */
        return `${prefix}pt-1`;
      case 's':
        /**
         * pt-2
         * md:pt-2
         * lg:pt-2
         */
        return `${prefix}pt-2`;
      case 'md':
        /**
         * pt-3
         * md:pt-3
         * lg:pt-3
         */
        return `${prefix}pt-3`;
      case 'l':
        /**
         * pt-4
         * md:pt-4
         * lg:pt-4
         */
        return `${prefix}pt-4`;
      case 'xl':
        /**
         * pt-5
         * md:pt-5
         * lg:pt-5
         */
        return `${prefix}pt-5`;
      case '2xl':
        /**
         * pt-6
         * md:pt-6
         * lg:pt-6
         */
        return `${prefix}pt-6`;
      case '3xl':
        /**
         * pt-8
         * md:pt-8
         * lg:pt-8
         */
        return `${prefix}pt-8`;
      case '4xl':
        /**
         * pt-12
         * md:pt-12
         * lg:pt-12
         */
        return `${prefix}pt-12`;
      case '5xl':
        /**
         * pt-16
         * md:pt-16
         * lg:pt-16
         */
        return `${prefix}pt-16`;
      case '6xl':
        /**
         * pt-20
         * md:pt-20
         * lg:pt-20
         */
        return `${prefix}pt-20`;
      case '7xl':
        /**
         * pt-24
         * md:pt-24
         * lg:pt-24
         */
        return `${prefix}pt-24`;
      case '8xl':
        /**
         * pt-32
         * md:pt-32
         * lg:pt-32
         */
        return `${prefix}pt-32`;
      case '9xl':
        /**
         * pt-40
         * md:pt-40
         * lg:pt-40
         */
        return `${prefix}pt-40`;
      default:
        return '';
    }
  }

  //     Padding X
  getPaddingXClass(
    padding?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (padding) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * px-0.5
         * md:px-0.5
         * lg:px-0.5
         */
        return `${prefix}px-0.5`;
      case 'xs':
        /**
         * px-1
         * md:px-1
         * lg:px-1
         */
        return `${prefix}px-1`;
      case 's':
        /**
         * px-2
         * md:px-2
         * lg:px-2
         */
        return `${prefix}px-2`;
      case 'md':
        /**
         * px-3
         * md:px-3
         * lg:px-3
         */
        return `${prefix}px-3`;
      case 'l':
        /**
         * px-4
         * md:px-4
         * lg:px-4
         */
        return `${prefix}px-4`;
      case 'xl':
        /**
         * px-5
         * md:px-5
         * lg:px-5
         */
        return `${prefix}px-5`;
      case '2xl':
        /**
         * px-6
         * md:px-6
         * lg:px-6
         */
        return `${prefix}px-6`;
      case '3xl':
        /**
         * px-8
         * md:px-8
         * lg:px-8
         */
        return `${prefix}px-8`;
      case '4xl':
        /**
         * px-12
         * md:px-12
         * lg:px-12
         */
        return `${prefix}px-12`;
      case '5xl':
        /**
         * px-16
         * md:px-16
         * lg:px-16
         */
        return `${prefix}px-16`;
      case '6xl':
        /**
         * px-20
         * md:px-20
         * lg:px-20
         */
        return `${prefix}px-20`;
      case '7xl':
        /**
         * px-24
         * md:px-24
         * lg:px-24
         */
        return `${prefix}px-24`;
      case '8xl':
        /**
         * px-32
         * md:px-32
         * lg:px-32
         */
        return `${prefix}px-32`;
      case '9xl':
        /**
         * px-40
         * md:px-40
         * lg:px-40
         */
        return `${prefix}px-40`;
      default:
        return '';
    }
  }

  getPaddingLeftClass(
    padding?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (padding) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * pl-0.5
         * md:pl-0.5
         * lg:pl-0.5
         */
        return `${prefix}pl-0.5`;
      case 'xs':
        /**
         * pl-1
         * md:pl-1
         * lg:pl-1
         */
        return `${prefix}pl-1`;
      case 's':
        /**
         * pl-2
         * md:pl-2
         * lg:pl-2
         */
        return `${prefix}pl-2`;
      case 'md':
        /**
         * pl-3
         * md:pl-3
         * lg:pl-3
         */
        return `${prefix}pl-3`;
      case 'l':
        /**
         * pl-4
         * md:pl-4
         * lg:pl-4
         */
        return `${prefix}pl-4`;
      case 'xl':
        /**
         * pl-5
         * md:pl-5
         * lg:pl-5
         */
        return `${prefix}pl-5`;
      case '2xl':
        /**
         * pl-6
         * md:pl-6
         * lg:pl-6
         */
        return `${prefix}pl-6`;
      case '3xl':
        /**
         * pl-8
         * md:pl-8
         * lg:pl-8
         */
        return `${prefix}pl-8`;
      case '4xl':
        /**
         * pl-12
         * md:pl-12
         * lg:pl-12
         */
        return `${prefix}pl-12`;
      case '5xl':
        /**
         * pl-16
         * md:pl-16
         * lg:pl-16
         */
        return `${prefix}pl-16`;
      case '6xl':
        /**
         * pl-20
         * md:pl-20
         * lg:pl-20
         */
        return `${prefix}pl-20`;
      case '7xl':
        /**
         * pl-24
         * md:pl-24
         * lg:pl-24
         */
        return `${prefix}pl-24`;
      case '8xl':
        /**
         * pl-32
         * md:pl-32
         * lg:pl-32
         */
        return `${prefix}pl-32`;
      case '9xl':
        /**
         * pl-40
         * md:pl-40
         * lg:pl-40
         */
        return `${prefix}pl-40`;
      default:
        return '';
    }
  }

  getPaddingRightClass(
    padding?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (padding) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * pr-0.5
         * md:pr-0.5
         * lg:pr-0.5
         */
        return `${prefix}pr-0.5`;
      case 'xs':
        /**
         * pr-1
         * md:pr-1
         * lg:pr-1
         */
        return `${prefix}pr-1`;
      case 's':
        /**
         * pr-2
         * md:pr-2
         * lg:pr-2
         */
        return `${prefix}pr-2`;
      case 'md':
        /**
         * pr-3
         * md:pr-3
         * lg:pr-3
         */
        return `${prefix}pr-3`;
      case 'l':
        /**
         * pr-4
         * md:pr-4
         * lg:pr-4
         */
        return `${prefix}pr-4`;
      case 'xl':
        /**
         * pr-5
         * md:pr-5
         * lg:pr-5
         */
        return `${prefix}pr-5`;
      case '2xl':
        /**
         * pr-6
         * md:pr-6
         * lg:pr-6
         */
        return `${prefix}pr-6`;
      case '3xl':
        /**
         * pr-8
         * md:pr-8
         * lg:pr-8
         */
        return `${prefix}pr-8`;
      case '4xl':
        /**
         * pr-12
         * md:pr-12
         * lg:pr-12
         */
        return `${prefix}pr-12`;
      case '5xl':
        /**
         * pr-16
         * md:pr-16
         * lg:pr-16
         */
        return `${prefix}pr-16`;
      case '6xl':
        /**
         * pr-20
         * md:pr-20
         * lg:pr-20
         */
        return `${prefix}pr-20`;
      case '7xl':
        /**
         * pr-24
         * md:pr-24
         * lg:pr-24
         */
        return `${prefix}pr-24`;
      case '8xl':
        /**
         * pr-32
         * md:pr-32
         * lg:pr-32
         */
        return `${prefix}pr-32`;
      case '9xl':
        /**
         * pr-40
         * md:pr-40
         * lg:pr-40
         */
        return `${prefix}pr-40`;
      default:
        return '';
    }
  }

  // Flex Gap
  getGapClass(
    gap?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (gap) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * gap-0.5
         * md:gap-0.5
         * lg:gap-0.5
         */
        return `${prefix}gap-0.5`;
      case 'xs':
        /**
         * gap-1
         * md:gap-1
         * lg:gap-1
         */
        return `${prefix}gap-1`;
      case 's':
        /**
         * gap-2
         * md:gap-2
         * lg:gap-2
         */
        return `${prefix}gap-2`;
      case 'md':
        /**
         * gap-3
         * md:gap-3
         * lg:gap-3
         */
        return `${prefix}gap-3`;
      case 'l':
        /**
         * gap-4
         * md:gap-4
         * lg:gap-4
         */
        return `${prefix}gap-4`;
      case 'xl':
        /**
         * gap-5
         * md:gap-5
         * lg:gap-5
         */
        return `${prefix}gap-5`;
      case '2xl':
        /**
         * gap-6
         * md:gap-6
         * lg:gap-6
         */
        return `${prefix}gap-6`;
      case '3xl':
        /**
         * gap-8
         * md:gap-8
         * lg:gap-8
         */
        return `${prefix}gap-8`;
      case '4xl':
        /**
         * gap-12
         * md:gap-12
         * lg:gap-12
         */
        return `${prefix}gap-12`;
      case '5xl':
        /**
         * gap-16
         * md:gap-16
         * lg:gap-16
         */
        return `${prefix}gap-16`;
      case '6xl':
        /**
         * gap-20
         * md:gap-20
         * lg:gap-20
         */
        return `${prefix}gap-20`;
      case '7xl':
        /**
         * gap-24
         * md:gap-24
         * lg:gap-24
         */
        return `${prefix}gap-24`;
      case '8xl':
        /**
         * gap-32
         * md:gap-32
         * lg:gap-32
         */
        return `${prefix}gap-32`;
      case '9xl':
        /**
         * gap-40
         * md:gap-40
         * lg:gap-40
         */
        return `${prefix}gap-40`;
      default:
        return '';
    }
  }

  getFlexDirectionClass(
    direction?: AppFlexDirection,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (direction) {
      case 'unset':
        return '';
      case 'row':
        /**
         * flex-row
         * md:flex-row
         * lg:flex-row
         */
        return `${prefix}flex-row`;
      case 'row-reverse':
        /**
         * flex-row-reverse
         * md:flex-row-reverse
         * lg:flex-row-reverse
         */
        return `${prefix}flex-row-reverse`;
      case 'column':
        /**
         * flex-col
         * md:flex-col
         * lg:flex-col
         */
        return `${prefix}flex-col`;
      case 'column-reverse':
        /**
         * flex-col-reverse
         * md:flex-col-reverse
         * lg:flex-col-reverse
         */
        return `${prefix}flex-col-reverse`;
      default:
        return '';
    }
  }

  getFlexAlignClass(
    align?: AppFlexAlign,
    screenSize: AppTailwindResponsiveDevices = '',
  ) {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (align) {
      case 'unset':
        return '';
      case 'flex-start':
        /**
         * items-start
         * md:items-start
         * lg:items-start
         */
        return `${prefix}items-start`;
      case 'center':
        /**
         * items-center
         * md:items-center
         * lg:items-center
         */
        return `${prefix}items-center`;
      case 'flex-end':
        /**
         * items-end
         * md:items-end
         * lg:items-end
         */
        return `${prefix}items-end`;
      case 'baseline':
        /**
         * items-baseline
         * md:items-baseline
         * lg:items-baseline
         */
        return `${prefix}items-baseline`;
      case 'stretch':
        /**
         * items-stretch
         * md:items-stretch
         * lg:items-stretch
         */
        return `${prefix}items-stretch`;
      default:
        return '';
    }
  }

  getFlexJustifyClass(
    justify?: AppFlexJustify,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (justify) {
      case 'unset':
        return '';
      case 'flex-start':
        /**
         * justify-start
         * md:justify-start
         * lg:justify-start
         */
        return `${prefix}justify-start`;
      case 'center':
        /**
         * justify-center
         * md:justify-center
         * lg:justify-center
         */
        return `${prefix}justify-center`;
      case 'flex-end':
        /**
         * justify-end
         * md:justify-end
         * lg:justify-end
         */
        return `${prefix}justify-end`;
      case 'space-between':
        /**
         * justify-between
         * md:justify-between
         * lg:justify-between
         */
        return `${prefix}justify-between`;
      case 'space-around':
        /**
         * justify-around
         * md:justify-around
         * lg:justify-around
         */
        return `${prefix}justify-around`;
      case 'space-evenly':
        /**
         * justify-evenly
         * md:justify-evenly
         * lg:justify-evenly
         */
        return `${prefix}justify-evenly`;
      default:
        return '';
    }
  }

  getFlexWrapClass(
    wrap?: AppFlexWrap,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (wrap) {
      case 'unset':
        return '';
      case 'wrap':
        /**
         * flex-wrap
         * md:flex-wrap
         * lg:flex-wrap
         */
        return `${prefix}flex-wrap`;
      case 'wrap-reverse':
        /**
         * flex-wrap-reverse
         * md:flex-wrap-reverse
         * lg:flex-wrap-reverse
         */
        return `${prefix}flex-wrap-reverse`;
      case 'nowrap':
        /**
         * flex-nowrap
         * md:flex-nowrap
         * lg:flex-nowrap
         */
        return `${prefix}flex-nowrap`;
      default:
        return '';
    }
  }

  getBackgroundColorClass(
    color?: AppColorPalette,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (color) {
      case 'unset':
        return '';
      case 'primary':
        /**
         * bg-primary
         * md:bg-primary
         * lg:bg-primary
         */
        return `${prefix}bg-primary`;
      case 'secondary':
        /**
         * bg-secondary
         * md:bg-secondary
         * lg:bg-secondary
         */
        return `${prefix}bg-secondary`;
      case 'accent':
        /**
         * bg-accent
         * md:bg-accent
         * lg:bg-accent
         */
        return `${prefix}bg-accent`;
      case 'grey':
        /**
         * bg-grey
         * md:bg-grey
         * lg:bg-grey
         */
        return `${prefix}bg-grey`;
      case 'white':
        /**
         * bg-white
         * md:bg-white
         * lg:bg-white
         */
        return `${prefix}bg-white`;
      case 'transparent':
        /**
         * bg-transparent
         * md:bg-transparent
         * lg:bg-transparent
         */
        return `${prefix}bg-transparent`;
      default:
        return '';
    }
  }

  getBorderRadiusClass(
    borderRadius?: AppBorderRadiuses,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (borderRadius) {
      case 'unset':
        return '';
      case 'none':
        /**
         * rounded-none
         * md:rounded-none
         * lg:rounded-none
         */
        return `${prefix}rounded-none`;
      case 'sm':
        /**
         * rounded-sm
         * md:rounded-sm
         * lg:rounded-sm
         */
        return `${prefix}rounded-sm`;
      case 'md':
        /**
         * rounded-md
         * md:rounded-md
         * lg:rounded-md
         */
        return `${prefix}rounded-md`;
      case 'lg':
        /**
         * rounded-lg
         * md:rounded-lg
         * lg:rounded-lg
         */
        return `${prefix}rounded-lg`;
      case 'xl':
        /**
         * rounded-xl
         * md:rounded-xl
         * lg:rounded-xl
         */
        return `${prefix}rounded-xl`;
      case '2xl':
        /**
         * rounded-2xl
         * md:rounded-2xl
         * lg:rounded-2xl
         */
        return `${prefix}rounded-2xl`;
      case '3xl':
        /**
         * rounded-3xl
         * md:rounded-3xl
         * lg:rounded-3xl
         */
        return `${prefix}rounded-3xl`;
      case 'full':
        /**
         * rounded-full
         * md:rounded-full
         * lg:rounded-full
         */
        return `${prefix}rounded-full`;
      default:
        return '';
    }
  }

  getMarginAlignClass(
    align?: AppContainerAlign,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (align) {
      case 'unset':
        return '';
      case 'center':
        /**
         * mx-auto
         * md:mx-auto
         * lg:mx-auto
         */
        return `${prefix}mx-auto`;
      case 'left':
        /**
         * ml-0
         * md:ml-0
         * lg:ml-0
         */
        return `${prefix}ml-0`;
      case 'right':
        /**
         * mr-0
         * md:mr-0
         * lg:mr-0
         */
        return `${prefix}mr-0 ml-auto`;
      default:
        return '';
    }
  }

  getImageMaxWidthClass(
    width?: AppImagesFixedWidths,
    screenSize: AppTailwindResponsiveDevices = '',
  ) {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (width) {
      case 'unset':
        return '';
      case '1rem':
        /**
         * max-w-4
         * md:max-w-4
         * lg:max-w-4
         */
        return `${prefix}max-w-4`;
      case '2rem':
        /**
         * max-w-8
         * md:max-w-8
         * lg:max-w-8
         */
        return `${prefix}max-w-8`;
      case '3rem':
        /**
         * max-w-12
         * md:max-w-12
         * lg:max-w-12
         */
        return `${prefix}max-w-12`;
      case '4rem':
        /**
         * max-w-16
         * md:max-w-16
         * lg:max-w-16
         */
        return `${prefix}max-w-16`;
      case '5rem':
        /**
         * max-w-20
         * md:max-w-20
         * lg:max-w-20
         */
        return `${prefix}max-w-20`;
      case '6rem':
        /**
         * max-w-24
         * md:max-w-24
         * lg:max-w-24
         */
        return `${prefix}max-w-24`;

      case '7rem':
        /**
         * max-w-28
         * md:max-w-28
         * lg:max-w-28
         */
        return `${prefix}max-w-28`;
      case '8rem':
        /**
         * max-w-32
         * md:max-w-32
         * lg:max-w-32
         */
        return `${prefix}max-w-32`;

      case '9rem':
        /**
         * max-w-36
         * md:max-w-36
         * lg:max-w-36
         */
        return `${prefix}max-w-36`;
      case '10rem':
        /**
         * max-w-40
         * md:max-w-40
         * lg:max-w-40
         */
        return `${prefix}max-w-40`;
      case '11rem':
        /**
         * max-w-44
         * md:max-w-44
         * lg:max-w-44
         */
        return `${prefix}max-w-44`;
      case '12rem':
        /**
         * max-w-48
         * md:max-w-48
         * lg:max-w-48
         */
        return `${prefix}max-w-48`;
      case '14rem':
        /**
         * max-w-56
         * md:max-w-56
         * lg:max-w-56
         */
        return `${prefix}max-w-56`;
      case '16rem':
        /**
         * max-w-64
         * md:max-w-64
         * lg:max-w-64
         */
        return `${prefix}max-w-64`;
      case '18rem':
        /**
         * max-w-72
         * md:max-w-72
         * lg:max-w-72
         */
        return `${prefix}max-w-72`;
      case '20rem':
        /**
         * max-w-xs
         * md:max-w-xs
         * lg:max-w-xs
         */
        return `${prefix}max-w-xs`;
      case '24rem':
        /**
         * max-w-sm
         * md:max-w-sm
         * lg:max-w-sm
         */
        return `${prefix}max-w-sm`;
      case '28rem':
        /**
         * max-w-md
         * md:max-w-md
         * lg:max-w-md
         */
        return `${prefix}max-w-md`;
      case '32rem':
        /**
         * max-w-lg
         * md:max-w-lg
         * lg:max-w-lg
         */
        return `${prefix}max-w-lg`;
      case '36rem':
        /**
         * max-w-xl
         * md:max-w-xl
         * lg:max-w-xl
         */
        return `${prefix}max-w-xl`;
      case '42rem':
        /**
         * max-w-2xl
         * md:max-w-2xl
         * lg:max-w-2xl
         */
        return `${prefix}max-w-2xl`;
      case '48rem':
        /**
         * max-w-3xl
         * md:max-w-3xl
         * lg:max-w-3xl
         */
        return `${prefix}max-w-3xl`;
      case '56rem':
        /**
         * max-w-4xl
         * md:max-w-4xl
         * lg:max-w-4xl
         */
        return `${prefix}max-w-4xl`;
      case '64rem':
        /**
         * max-w-5xl
         * md:max-w-5xl
         * lg:max-w-5xl
         */
        return `${prefix}max-w-5xl`;
      case '72rem':
        /**
         * max-w-6xl
         * md:max-w-6xl
         * lg:max-w-6xl
         */
        return `${prefix}max-w-6xl`;
      case '80rem':
        /**
         * max-w-7xl
         * md:max-w-7xl
         * lg:max-w-7xl
         */
        return `${prefix}max-w-7xl`;

      default:
        return '';
    }
  }

  getFluidWidths(
    width: AppImageWidthsPercentage,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (width) {
      case 'unset':
        return '';
      case '12/12':
        /**
         * w-full
         * md:w-full
         * lg:w-full
         */
        return `${prefix}w-full`;
      case '11/12':
        /**
         * w-11/12
         * md:w-11/12
         * lg:w-11/12
         */
        return `${prefix}w-11/12`;
      case '10/12':
        /**
         * w-10/12
         * md:w-10/12
         * lg:w-10/12
         */
        return `${prefix}w-10/12`;
      case '9/12':
        /**
         * w-9/12
         * md:w-9/12
         * lg:w-9/12
         */
        return `${prefix}w-9/12`;
      case '8/12':
        /**
         * w-8/12
         * md:w-8/12
         * lg:w-8/12
         */
        return `${prefix}w-8/12`;
      case '7/12':
        /**
         * w-7/12
         * md:w-7/12
         * lg:w-7/12
         */
        return `${prefix}w-7/12`;
      case '6/12':
        /**
         * w-6/12
         * md:w-6/12
         * lg:w-6/12
         */
        return `${prefix}w-6/12`;
      case '5/12':
        /**
         * w-5/12
         * md:w-5/12
         * lg:w-5/12
         */
        return `${prefix}w-5/12`;
      case '4/12':
        /**
         * w-4/12
         * md:w-4/12
         * lg:w-4/12
         */
        return `${prefix}w-4/12`;
      case '3/12':
        /**
         * w-3/12
         * md:w-3/12
         * lg:w-3/12
         */
        return `${prefix}w-3/12`;
      case '2/12':
        /**
         * w-2/12
         * md:w-2/12
         * lg:w-2/12
         */
        return `${prefix}w-2/12`;
      case '1/12':
        /**
         * w-1/12
         * md:w-1/12
         * lg:w-1/12
         */
        return `${prefix}w-1/12`;
      default:
        return '';
    }
  }

  getGradientDirectionClass(
    direction?: AppGradientDirections,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (direction) {
      case 'unset':
        return '';
      case 'left':
        /**
         * bg-gradient-to-l
         * md:bg-gradient-to-l
         * lg:bg-gradient-to-l
         */
        return `${prefix}bg-gradient-to-l`;
      case 'right':
        /**
         * bg-gradient-to-r
         * md:bg-gradient-to-r
         * lg:bg-gradient-to-r
         */
        return `${prefix}bg-gradient-to-r`;
      case 'top':
        /**
         * bg-gradient-to-t
         * md:bg-gradient-to-t
         * lg:bg-gradient-to-t
         */
        return `${prefix}bg-gradient-to-t`;
      case 'bottom':
        /**
         * bg-gradient-to-b
         * md:bg-gradient-to-b
         * lg:bg-gradient-to-b
         */
        return `${prefix}bg-gradient-to-b`;
      case 'bottom-left':
        /**
         * bg-gradient-to-bl
         * md:bg-gradient-to-bl
         * lg:bg-gradient-to-bl
         */
        return `${prefix}bg-gradient-to-bl`;
      case 'bottom-right':
        /**
         * bg-gradient-to-br
         * md:bg-gradient-to-br
         * lg:bg-gradient-to-br
         */
        return `${prefix}bg-gradient-to-br`;
      case 'top-left':
        /**
         * bg-gradient-to-tl
         * md:bg-gradient-to-tl
         * lg:bg-gradient-to-tl
         */
        return `${prefix}bg-gradient-to-tl`;
      case 'top-right':
        /**
         * bg-gradient-to-tr
         * md:bg-gradient-to-tr
         * lg:bg-gradient-to-tr
         */
        return `${prefix}bg-gradient-to-tr`;
      default:
        return '';
    }
  }

  getGradientColorClass(gradientColor?: AppContainerPalette): string {
    if (gradientColor === 'primary') {
      return 'from-primary-gradient-start to-primary-gradient-end';
    } else if (gradientColor === 'secondary') {
      return 'from-accent-gradient-start to-accent-gradient-end';
    } else return '';
  }

  getBackgroundPositionClass(
    position?: AppBackgroundPositions,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (position) {
      case 'unset':
        return '';
      case 'bottom':
        /**
         * bg-bottom
         * md:bg-bottom
         * lg:bg-bottom
         */
        return `${prefix}bg-bottom`;
      case 'center':
        /**
         * bg-center
         * md:bg-center
         * lg:bg-center
         */
        return `${prefix}bg-center`;
      case 'left':
        /**
         * bg-left
         * md:bg-left
         * lg:bg-left
         */
        return `${prefix}bg-left`;
      case 'left-bottom':
        /**
         * bg-left-bottom
         * md:bg-left-bottom
         * lg:bg-left-bottom
         */
        return `${prefix}bg-left-bottom`;
      case 'left-top':
        /**
         * bg-left-top
         * md:bg-left-top
         * lg:bg-left-top
         */
        return `${prefix}bg-left-top`;
      case 'right':
        /**
         * bg-right
         * md:bg-right
         * lg:bg-right
         */
        return `${prefix}bg-right`;
      case 'right-bottom':
        /**
         * bg-right-bottom
         * md:bg-right-bottom
         * lg:bg-right-bottom
         */
        return `${prefix}bg-right-bottom`;
      case 'right-top':
        /**
         * bg-right-top
         * md:bg-right-top
         * lg:bg-right-top
         */
        return `${prefix}bg-right-top`;
      case 'top':
        /**
         * bg-top
         * md:bg-top
         * lg:bg-top
         */
        return `${prefix}bg-top`;
      default:
        return '';
    }
  }

  getBackgroundSizeClass(
    size: AppBackgroundSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (size) {
      case 'unset':
        return '';
      case 'auto':
        /**
         * bg-auto
         * md:bg-auto
         * lg:bg-auto
         */
        return `${prefix}bg-auto`;
      case 'cover':
        /**
         * bg-cover
         * md:bg-cover
         * lg:bg-cover
         */
        return `${prefix}bg-cover`;
      case 'contain':
        /**
         * bg-contain
         * md:bg-contain
         * lg:bg-contain
         */
        return `${prefix}bg-contain`;
      default:
        return '';
    }
  }

  getWidthClass(
    width?: AppWidths,
    screenSize: AppTailwindResponsiveDevices = '',
  ) {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (width) {
      case 'unset':
        return '';
      case '25%':
        /**
         * w-1/4
         * md:w-1/4
         * lg:w-1/4
         */
        return `${prefix}w-1/4`;
      case '50%':
        /**
         * w-1/2
         * md:w-1/2
         * lg:w-1/2
         */
        return `${prefix}w-1/2`;
      case '100%':
        /**
         * w-full
         * md:w-full
         * lg:w-full
         */
        return `${prefix}w-1/2`;
      case '1rem':
        /**
         * w-4
         * md:w-4
         * lg:w-4
         */
        return `${prefix}w-4`;
      case '2rem':
        /**
         * w-8
         * md:w-8
         * lg:w-8
         */
        return `${prefix}w-8`;
      case '3rem':
        /**
         * w-12
         * md:w-12
         * lg:w-12
         */
        return `${prefix}w-12`;
      case '4rem':
        /**
         * w-16
         * md:w-16
         * lg:w-16
         */
        return `${prefix}w-16`;
      case '5rem':
        /**
         * w-20
         * md:w-20
         * lg:w-20
         */
        return `${prefix}w-20`;
      case '6rem':
        /**
         * w-24
         * md:w-24
         * lg:w-24
         */
        return `${prefix}w-24`;
      case '7rem':
        /**
         * w-28
         * md:w-28
         * lg:w-28
         */
        return `${prefix}w-28`;
      case '8rem':
        /**
         * w-32
         * md:w-32
         * lg:w-32
         */
        return `${prefix}w-32`;
      case '9rem':
        /**
         * w-36
         * md:w-36
         * lg:w-36
         */
        return `${prefix}w-36`;
      case '10rem':
        /**
         * w-40
         * md:w-40
         * lg:w-40
         */
        return `${prefix}w-40`;
      case '11rem':
        /**
         * w-44
         * md:w-44
         * lg:w-44
         */
        return `${prefix}w-44`;
      case '12rem':
        /**
         * w-48
         * md:w-48
         * lg:w-48
         */
        return `${prefix}w-48`;
      case '14rem':
        /**
         * w-56
         * md:w-56
         * lg:w-56
         */
        return `${prefix}w-56`;
      case '16rem':
        /**
         * w-64
         * md:w-64
         * lg:w-64
         */
        return `${prefix}w-64`;
      case '18rem':
        /**
         * w-72
         * md:w-72
         * lg:w-72
         */
        return `${prefix}w-72`;
      case '20rem':
        /**
         * w-80
         * md:w-80
         * lg:w-80
         */
        return `${prefix}w-80`;
      case '24rem':
        /**
         * w-96
         * md:w-96
         * lg:w-96
         */
        return `${prefix}w-96`;
      case 'none':
        /**
         * w-0
         * md:w-0
         * lg:w-0
         */
        return `${prefix}w-0`;
      default:
        return '';
    }
  }

  getMaxWidthClass(
    width?: AppMaxFixedWidths,
    screenSize: AppTailwindResponsiveDevices = '',
  ) {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (width) {
      case 'unset':
        return '';
      case '25%':
        /**
         * max-w-1/4
         * md:max-w-1/4
         * lg:max-w-1/4
         */
        return `${prefix}max-w-1/4`;
      case '50%':
        /**
         * max-w-1/2
         * md:max-w-1/2
         * lg:max-w-1/2
         */
        return `${prefix}max-w-1/2`;
      case '100%':
        /**
         * max-w-full
         * md:max-w-full
         * lg:max-w-full
         */
        return `${prefix}max-w-full`;
      case '1rem':
        /**
         * max-w-4
         * md:max-w-4
         * lg:max-w-4
         */
        return `${prefix}max-w-4`;
      case '2rem':
        /**
         * max-w-8
         * md:max-w-8
         * lg:max-w-8
         */
        return `${prefix}max-w-8`;
      case '3rem':
        /**
         * max-w-12
         * md:max-w-12
         * lg:max-w-12
         */
        return `${prefix}max-w-12`;
      case '4rem':
        /**
         * max-w-16
         * md:max-w-16
         * lg:max-w-16
         */
        return `${prefix}max-w-16`;
      case '5rem':
        /**
         * max-w-20
         * md:max-w-20
         * lg:max-w-20
         */
        return `${prefix}max-w-20`;
      case '6rem':
        /**
         * max-w-24
         * md:max-w-24
         * lg:max-w-24
         */
        return `${prefix}max-w-24`;
      case '7rem':
        /**
         * max-w-28
         * md:max-w-28
         * lg:max-w-28
         */
        return `${prefix}max-w-28`;
      case '8rem':
        /**
         * max-w-32
         * md:max-w-32
         * lg:max-w-32
         */
        return `${prefix}max-w-32`;
      case '9rem':
        /**
         * max-w-36
         * md:max-w-36
         * lg:max-w-36
         */
        return `${prefix}max-w-36`;
      case '10rem':
        /**
         * max-w-40
         * md:max-w-40
         * lg:max-w-40
         */
        return `${prefix}max-w-40`;
      case '11rem':
        /**
         * max-w-44
         * md:max-w-44
         * lg:max-w-44
         */
        return `${prefix}max-w-44`;
      case '12rem':
        /**
         * max-w-48
         * md:max-w-48
         * lg:max-w-48
         */
        return `${prefix}max-w-48`;
      case '14rem':
        /**
         * max-w-56
         * md:max-w-56
         * lg:max-w-56
         */
        return `${prefix}max-w-56`;
      case '16rem':
        /**
         * max-w-64
         * md:max-w-64
         * lg:max-w-64
         */
        return `${prefix}max-w-64`;
      case '18rem':
        /**
         * max-w-72
         * md:max-w-72
         * lg:max-w-72
         */
        return `${prefix}max-w-72`;
      case '20rem':
        /**
         * max-w-xs
         * md:max-w-xs
         * lg:max-w-xs
         */
        return `${prefix}max-w-xs`;
      case '24rem':
        /**
         * max-w-sm
         * md:max-w-sm
         * lg:max-w-sm
         */
        return `${prefix}max-w-sm`;
      case '28rem':
        /**
         * max-w-md
         * md:max-w-md
         * lg:max-w-md
         */
        return `${prefix}max-w-md`;
      case '32rem':
        /**
         * max-w-lg
         * md:max-w-lg
         * lg:max-w-lg
         */
        return `${prefix}max-w-lg`;
      case '36rem':
        /**
         * max-w-xl
         * md:max-w-xl
         * lg:max-w-xl
         */
        return `${prefix}max-w-xl`;
      case '42rem':
        /**
         * max-w-2xl
         * md:max-w-2xl
         * lg:max-w-2xl
         */
        return `${prefix}max-w-2xl`;
      case '48rem':
        /**
         * max-w-3xl
         * md:max-w-3xl
         * lg:max-w-3xl
         */
        return `${prefix}max-w-3xl`;
      case '56rem':
        /**
         * max-w-4xl
         * md:max-w-4xl
         * lg:max-w-4xl
         */
        return `${prefix}max-w-4xl`;
      case '64rem':
        /**
         * max-w-5xl
         * md:max-w-5xl
         * lg:max-w-5xl
         */
        return `${prefix}max-w-5xl`;
      case '72rem':
        /**
         * max-w-6xl
         * md:max-w-6xl
         * lg:max-w-6xl
         */
        return `${prefix}max-w-6xl`;
      case '80rem':
        /**
         * max-w-7xl
         * md:max-w-7xl
         * lg:max-w-7xl
         */
        return `${prefix}max-w-7xl`;
      case '90rem':
        /**
         * max-w-[90rem]
         * md:max-w-[90rem]
         * lg:max-w-[90rem]
         */
        return `${prefix}max-w-[90rem]`;
      default:
        return '';
    }
  }

  getHeightClass(
    height?: AppHeights,
    screenSize: AppTailwindResponsiveDevices = '',
  ) {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (height) {
      case 'unset':
        return '';
      case '1rem':
        /**
         * h-4
         * md:h-4
         * lg:h-4
         */
        return `${prefix}h-4`;
      case '2rem':
        /**
         * h-8
         * md:h-8
         * lg:h-8
         */
        return `${prefix}h-8`;
      case '3rem':
        /**
         * h-12
         * md:h-12
         * lg:h-12
         */
        return `${prefix}h-12`;
      case '4rem':
        /**
         * h-16
         * md:h-16
         * lg:h-16
         */
        return `${prefix}h-16`;
      case '5rem':
        /**
         * h-20
         * md:h-20
         * lg:h-20
         */
        return `${prefix}h-20`;
      case '6rem':
        /**
         * h-24
         * md:h-24
         * lg:h-24
         */
        return `${prefix}h-24`;
      case '7rem':
        /**
         * h-28
         * md:h-28
         * lg:h-28
         */
        return `${prefix}h-28`;
      case '8rem':
        /**
         * h-32
         * md:h-32
         * lg:h-32
         */
        return `${prefix}h-32`;
      case '9rem':
        /**
         * h-36
         * md:h-36
         * lg:h-36
         */
        return `${prefix}h-36`;
      case '10rem':
        /**
         * h-40
         * md:h-40
         * lg:h-40
         */
        return `${prefix}h-40`;
      case '11rem':
        /**
         * h-44
         * md:h-44
         * lg:h-44
         */
        return `${prefix}h-44`;
      case '12rem':
        /**
         * h-48
         * md:h-48
         * lg:h-48
         */
        return `${prefix}h-48`;
      case '14rem':
        /**
         * h-56
         * md:h-56
         * lg:h-56
         */
        return `${prefix}h-56`;
      case '16rem':
        /**
         * h-64
         * md:h-64
         * lg:h-64
         */
        return `${prefix}h-64`;
      case '18rem':
        /**
         * h-72
         * md:h-72
         * lg:h-72
         */
        return `${prefix}h-72`;
      case '20rem':
        /**
         * h-80
         * md:h-80
         * lg:h-80
         */
        return `${prefix}h-80`;
      case '24rem':
        /**
         * h-96
         * md:h-96
         * lg:h-96
         */
        return `${prefix}h-96`;
      case 'none':
        /**
         * h-0
         * md:h-0
         * lg:h-0
         */
        return `${prefix}h-0`;
      default:
        return '';
    }
  }

  getBackgroundColorShadesClass(
    color?: AppColorShades,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (color) {
      case 'unset':
        return '';
      case 'primary-90':
        /**
         * bg-primary90
         * md:bg-primary90
         * lg:bg-primary90
         */
        return `${prefix}bg-primary`;
      case 'primary-80':
        /**
         * bg-primary80
         * md:bg-primary80
         * lg:bg-primary80
         */
        return `${prefix}bg-primary80`;
      case 'primary-70':
        /**
         * bg-primary70
         * md:bg-primary70
         * lg:bg-primary70
         */
        return `${prefix}bg-primary70`;
      case 'primary-60':
        /**
         * bg-primary60
         * md:bg-primary60
         * lg:bg-primary60
         */
        return `${prefix}bg-primary60`;
      case 'primary-50':
        /**
         * bg-primary50
         * md:bg-primary50
         * lg:bg-primary50
         */
        return `${prefix}bg-primary50`;
      case 'primary-40':
        /**
         * bg-primary40
         * md:bg-primary40
         * lg:bg-primary40
         */
        return `${prefix}bg-primary40`;
      case 'primary-30':
        /**
         * bg-primary30
         * md:bg-primary30
         * lg:bg-primary30
         */
        return `${prefix}bg-primary30`;
      case 'primary-20':
        /**
         * bg-primary20
         * md:bg-primary20
         * lg:bg-primary20
         */
        return `${prefix}bg-primary20`;
      case 'primary-10':
        /**
         * bg-primary10
         * md:bg-primary10
         * lg:bg-primary10
         */
        return `${prefix}bg-primary10`;
      case 'secondary-10':
        /**
         * bg-secondary10
         * md:bg-secondary10
         * lg:bg-secondary10
         */
        return `${prefix}bg-secondary10`;
      case 'secondary-20':
        /**
         * bg-secondary20
         * md:bg-secondary20
         * lg:bg-secondary20
         */
        return `${prefix}bg-secondary20`;
      case 'secondary-30':
        /**
         * bg-secondary30
         * md:bg-secondary30
         * lg:bg-secondary30
         */
        return `${prefix}bg-secondary30`;
      case 'secondary-40':
        /**
         * bg-secondary40
         * md:bg-secondary40
         * lg:bg-secondary40
         */
        return `${prefix}bg-secondary40`;
      case 'secondary-50':
        /**
         * bg-secondary50
         * md:bg-secondary50
         * lg:bg-secondary50
         */
        return `${prefix}bg-secondary50`;
      case 'secondary-60':
        /**
         * bg-secondary60
         * md:bg-secondary60
         * lg:bg-secondary60
         */
        return `${prefix}bg-secondary60`;
      case 'secondary-70':
        /**
         * bg-secondary70
         * md:bg-secondary70
         * lg:bg-secondary70
         */
        return `${prefix}bg-secondary70`;
      case 'secondary-80':
        /**
         * bg-secondary80
         * md:bg-secondary80
         * lg:bg-secondary80
         */
        return `${prefix}bg-secondary80`;
      case 'secondary-90':
        /**
         * bg-secondary90
         * md:bg-secondary90
         * lg:bg-secondary90
         */
        return `${prefix}bg-secondary90`;
      case 'accent-10':
        /**
         * bg-accent10
         * md:bg-accent10
         * lg:bg-accent10
         */
        return `${prefix}bg-accent10`;
      case 'accent-20':
        /**
         * bg-accent20
         * md:bg-accent20
         * lg:bg-accent20
         */
        return `${prefix}bg-accent20`;
      case 'accent-30':
        /**
         * bg-accent30
         * md:bg-accent30
         * lg:bg-accent30
         */
        return `${prefix}bg-accent30`;
      case 'accent-40':
        /**
         * bg-accent40
         * md:bg-accent40
         * lg:bg-accent40
         */
        return `${prefix}bg-accent40`;
      case 'accent-50':
        /**
         * bg-accent50
         * md:bg-accent50
         * lg:bg-accent50
         */
        return `${prefix}bg-accent50`;
      case 'accent-60':
        /**
         * bg-accent60
         * md:bg-accent60
         * lg:bg-accent60
         */
        return `${prefix}bg-accent60`;
      case 'accent-70':
        /**
         * bg-accent70
         * md:bg-accent70
         * lg:bg-accent70
         */
        return `${prefix}bg-accent70`;
      case 'accent-80':
        /**
         * bg-accent80
         * md:bg-accent80
         * lg:bg-accent80
         */
        return `${prefix}bg-accent80`;
      case 'accent-90':
        /**
         * bg-accent90
         * md:bg-accent90
         * lg:bg-accent90
         */
        return `${prefix}bg-accent90`;
      case 'grey-10':
        /**
         * bg-grey10
         * md:bg-grey10
         * lg:bg-grey10
         */
        return `${prefix}bg-grey10`;
      case 'grey-20':
        /**
         * bg-grey20
         * md:bg-grey20
         * lg:bg-grey20
         */
        return `${prefix}bg-grey20`;
      case 'grey-30':
        /**
         * bg-grey30
         * md:bg-grey30
         * lg:bg-grey30
         */
        return `${prefix}bg-grey30`;
      case 'grey-40':
        /**
         * bg-grey40
         * md:bg-grey40
         * lg:bg-grey40
         */
        return `${prefix}bg-grey40`;
      case 'grey-50':
        /**
         * bg-grey50
         * md:bg-grey50
         * lg:bg-grey50
         */
        return `${prefix}bg-grey50`;
      case 'grey-60':
        /**
         * bg-grey60
         * md:bg-grey60
         * lg:bg-grey60
         */
        return `${prefix}bg-grey60`;
      case 'grey-70':
        /**
         * bg-grey70
         * md:bg-grey70
         * lg:bg-grey70
         */
        return `${prefix}bg-grey70`;
      case 'grey-80':
        /**
         * bg-grey80
         * md:bg-grey80
         * lg:bg-grey80
         */
        return `${prefix}bg-grey80`;
      case 'grey-90':
        /**
         * bg-grey90
         * md:bg-grey90
         * lg:bg-grey90
         */
        return `${prefix}bg-grey90`;
      default:
        return '';
    }
  }

  getBoxShadowClass(
    shadow?: AppBoxShadows,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (shadow) {
      case 'unset':
        return '';
      case 'sm':
        /**
         * shadow-sm
         * md:shadow-sm
         * lg:shadow-sm
         */
        return `${prefix}shadow-sm`;
      case 'normal':
        /**
         * shadow
         * md:shadow
         * lg:shadow
         */
        return `${prefix}shadow`;
      case 'md':
        /**
         * shadow-md
         * md:shadow-md
         * lg:shadow-md
         */
        return `${prefix}shadow-md`;
      case 'lg':
        /**
         * shadow-lg
         * md:shadow-lg
         * lg:shadow-lg
         */
        return `${prefix}shadow-lg`;
      case 'xl':
        /**
         * shadow-xl
         * md:shadow-xl
         * lg:shadow-xl
         */
        return `${prefix}shadow-xl`;
      case '2xl':
        /**
         * shadow-2xl
         * md:shadow-2xl
         * lg:shadow-2xl
         */
        return `${prefix}shadow-2xl`;
      case 'inner':
        /**
         * shadow-inner
         * md:shadow-inner
         * lg:shadow-inner
         */
        return `${prefix}shadow-inner`;
      case 'none':
        /**
         * shadow-none
         * md:shadow-none
         * lg:shadow-none
         */
        return `${prefix}shadow-none`;
      case 'tarif-mobile-button':
        /**
         * shadow-tarif-mobile-button
         * md:shadow-tarif-mobile-button
         * lg:shadow-tarif-mobile-button
         */
        return `${prefix}shadow-tarif-mobile-button`;
      case 'checkout-iframe':
        /**
         * shadow-checkout-iframe
         * md:shadow-checkout-iframe
         * lg:shadow-checkout-iframe
         */
        return `${prefix}shadow-checkout-iframe`;
      default:
        return '';
    }
  }

  getColSpanClass(
    span?: AppColSpans,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (span) {
      case 1:
        /**
         * col-span-1
         * md:col-span-1
         * lg:col-span-1
         */
        return `${prefix}col-span-1`;
      case 2:
        /**
         * col-span-2
         * md:col-span-2
         * lg:col-span-2
         */
        return `${prefix}col-span-2`;
      case 3:
        /**
         * col-span-3
         * md:col-span-3
         * lg:col-span-3
         */
        return `${prefix}col-span-3`;
      case 4:
        /**
         * col-span-4
         * md:col-span-4
         * lg:col-span-4
         */
        return `${prefix}col-span-4`;
      case 5:
        /**
         * col-span-5
         * md:col-span-5
         * lg:col-span-5
         */
        return `${prefix}col-span-5`;
      case 6:
        /**
         * col-span-6
         * md:col-span-6
         * lg:col-span-6
         */
        return `${prefix}col-span-6`;
      case 7:
        /**
         * col-span-7
         * md:col-span-7
         * lg:col-span-7
         */
        return `${prefix}col-span-7`;
      case 8:
        /**
         * col-span-8
         * md:col-span-8
         * lg:col-span-8
         */
        return `${prefix}col-span-8`;
      case 9:
        /**
         * col-span-9
         * md:col-span-9
         * lg:col-span-9
         */
        return `${prefix}col-span-9`;
      case 10:
        /**
         * col-span-10
         * md:col-span-10
         * lg:col-span-10
         */
        return `${prefix}col-span-10`;
      case 11:
        /**
         * col-span-11
         * md:col-span-11
         * lg:col-span-11
         */
        return `${prefix}col-span-11`;
      case 12:
        /**
         * col-span-12
         * md:col-span-12
         * lg:col-span-12
         */
        return `${prefix}col-span-12`;
      default:
        return '';
    }
  }

  getColXGapClass(
    gap?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (gap) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * gap-x-0.5
         * md:gap-x-0.5
         * lg:gap-x-0.5
         */
        return `${prefix}gap-x-0.5`;
      case 'xs':
        /**
         * gap-x-1
         * md:gap-x-1
         * lg:gap-x-1
         */
        return `${prefix}gap-x-1`;
      case 's':
        /**
         * gap-x-2
         * md:gap-x-2
         * lg:gap-x-2
         */
        return `${prefix}gap-x-2`;
      case 'md':
        /**
         * gap-x-3
         * md:gap-x-3
         * lg:gap-x-3
         */
        return `${prefix}gap-x-3`;
      case 'l':
        /**
         * gap-x-4
         * md:gap-x-4
         * lg:gap-x-4
         */
        return `${prefix}gap-x-4`;
      case 'xl':
        /**
         * gap-x-5
         * md:gap-x-5
         * lg:gap-x-5
         */
        return `${prefix}gap-x-5`;
      case '2xl':
        /**
         * gap-x-6
         * md:gap-x-6
         * lg:gap-x-6
         */
        return `${prefix}gap-x-6`;
      case '3xl':
        /**
         * gap-x-8
         * md:gap-x-8
         * lg:gap-x-8
         */
        return `${prefix}gap-x-8`;
      case '4xl':
        /**
         * gap-x-12
         * md:gap-x-12
         * lg:gap-x-12
         */
        return `${prefix}gap-x-12`;
      case '5xl':
        /**
         * gap-x-16
         * md:gap-x-16
         * lg:gap-x-16
         */
        return `${prefix}gap-x-16`;
      case '6xl':
        /**
         * gap-x-20
         * md:gap-x-20
         * lg:gap-x-20
         */
        return `${prefix}gap-x-20`;
      case '7xl':
        /**
         * gap-x-24
         * md:gap-x-24
         * lg:gap-x-24
         */
        return `${prefix}gap-x-24`;
      case '8xl':
        /**
         * gap-x-32
         * md:gap-x-32
         * lg:gap-x-32
         */
        return `${prefix}gap-x-32`;
      case '9xl':
        /**
         * gap-x-40
         * md:gap-x-40
         * lg:gap-x-40
         */
        return `${prefix}gap-x-40`;
      default:
        return '';
    }
  }

  getColYGapClass(
    gap?: AppSpacingSizes,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (gap) {
      case 'unset':
        return '';
      case 'xxs':
        /**
         * gap-y-0.5
         * md:gap-y-0.5
         * lg:gap-y-0.5
         */
        return `${prefix}gap-y-0.5`;
      case 'xs':
        /**
         * gap-y-1
         * md:gap-y-1
         * lg:gap-y-1
         */
        return `${prefix}gap-y-1`;
      case 's':
        /**
         * gap-y-2
         * md:gap-y-2
         * lg:gap-y-2
         */
        return `${prefix}gap-y-2`;
      case 'md':
        /**
         * gap-y-3
         * md:gap-y-3
         * lg:gap-y-3
         */
        return `${prefix}gap-y-3`;
      case 'l':
        /**
         * gap-y-4
         * md:gap-y-4
         * lg:gap-y-4
         */
        return `${prefix}gap-y-4`;
      case 'xl':
        /**
         * gap-y-5
         * md:gap-y-5
         * lg:gap-y-5
         */
        return `${prefix}gap-y-5`;
      case '2xl':
        /**
         * gap-y-6
         * md:gap-y-6
         * lg:gap-y-6
         */
        return `${prefix}gap-y-6`;
      case '3xl':
        /**
         * gap-y-8
         * md:gap-y-8
         * lg:gap-y-8
         */
        return `${prefix}gap-y-8`;
      case '4xl':
        /**
         * gap-y-12
         * md:gap-y-12
         * lg:gap-y-12
         */
        return `${prefix}gap-y-12`;
      case '5xl':
        /**
         * gap-y-16
         * md:gap-y-16
         * lg:gap-y-16
         */
        return `${prefix}gap-y-16`;
      case '6xl':
        /**
         * gap-y-20
         * md:gap-y-20
         * lg:gap-y-20
         */
        return `${prefix}gap-y-20`;
      case '7xl':
        /**
         * gap-y-24
         * md:gap-y-24
         * lg:gap-y-24
         */
        return `${prefix}gap-y-24`;
      case '8xl':
        /**
         * gap-y-32
         * md:gap-y-32
         * lg:gap-y-32
         */
        return `${prefix}gap-y-32`;
      case '9xl':
        /**
         * gap-y-40
         * md:gap-y-40
         * lg:gap-y-40
         */
        return `${prefix}gap-y-40`;
      default:
        return '';
    }
  }

  getBackgroundOpacityClass(
    opacity?: AppBackgroundOpacities,
    screenSize: AppTailwindResponsiveDevices = '',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (opacity) {
      case 'unset':
        return '';
      case '0':
        /**
         * !bg-opacity-0
         * md:!bg-opacity-0
         * lg:!bg-opacity-0
         */
        return `${prefix}!bg-opacity-0`;
      case '10':
        /**
         * !bg-opacity-10
         * md:!bg-opacity-10
         * lg:!bg-opacity-10
         */
        return `${prefix}!bg-opacity-10`;
      case '20':
        /**
         * !bg-opacity-20
         * md:!bg-opacity-20
         * lg:!bg-opacity-20
         */
        return `${prefix}!bg-opacity-20`;
      case '30':
        /**
         * !bg-opacity-30
         * md:!bg-opacity-30
         * lg:!bg-opacity-30
         */
        return `${prefix}!bg-opacity-30`;
      case '40':
        /**
         * !bg-opacity-40
         * md:!bg-opacity-40
         * lg:!bg-opacity-40
         */
        return `${prefix}!bg-opacity-40`;
      case '50':
        /**
         * !bg-opacity-50
         * md:!bg-opacity-50
         * lg:!bg-opacity-50
         */
        return `${prefix}!bg-opacity-50`;
      case '60':
        /**
         * !bg-opacity-60
         * md:!bg-opacity-60
         * lg:!bg-opacity-60
         */
        return `${prefix}!bg-opacity-60`;
      case '70':
        /**
         * !bg-opacity-70
         * md:!bg-opacity-70
         * lg:!bg-opacity-70
         */
        return `${prefix}!bg-opacity-70`;
      case '80':
        /**
         * !bg-opacity-80
         * md:!bg-opacity-80
         * lg:!bg-opacity-80
         */
        return `${prefix}!bg-opacity-80`;
      case '90':
        /**
         * !bg-opacity-90
         * md:!bg-opacity-90
         * lg:!bg-opacity-90
         */
        return `${prefix}!bg-opacity-90`;
      case '100':
        /**
         * !bg-opacity-100
         * md:!bg-opacity-100
         * lg:!bg-opacity-100
         */
        return `${prefix}!bg-opacity-100`;
      default:
        return '';
    }
  }

  getDisplayClass(
    display?: AppDisplayTypes,
    screenSize: AppTailwindResponsiveDevices = '',
    displayType: AppTailwindDisplayType = 'block',
  ): string {
    const prefix: AppTailwindResponsivePrefixes =
      this.getResponsivePrefix(screenSize);
    switch (display) {
      case 'unset':
        return '';
      case 'hidden':
        /**
         * hidden
         * md:hidden
         * lg:hidden
         */
        return `${prefix}hidden`;
      case 'visible':
        /**
         * block
         * md:block
         * lg:block
         * flex
         * md:flex
         * lg:flex
         * inline-flex
         * md:inline-flex
         * lg:inline-flex
         * grid
         * md:grid
         * lg:grid
         * inline-grid
         * md:inline-grid
         * lg:inline-grid
         */
        return `${prefix}${displayType}`;
      case 'sr-only':
        /**
         * sr-only
         * md:sr-only
         * lg:sr-only
         */
        return `${prefix}sr-only`;
      case 'not-sr-only':
        /**
         * not-sr-only
         * md:not-sr-only
         * lg:not-sr-only
         */
        return `${prefix}not-sr-only`;
    }
    return '';
  }

  // Pre-build style functions to be used project-wide
  getBackgroundOpacityClassFunction(
    obj: Partial<backgroundOpacityInterface>,
  ): string {
    // code to call:

    // this.classes.getBackgroundOpacityClassFunction({
    //   bgOpacity: this.bgOpacity,
    //   bgOpacityTablet: this.bgOpacityTablet,
    //   bgOpacityDesktop: this.bgOpacityDesktop,
    // }),

    return [
      this.getBackgroundOpacityClass(obj?.bgOpacity),
      this.getBackgroundOpacityClass(obj?.bgOpacityTablet, 'tablet'),
      this.getBackgroundOpacityClass(obj?.bgOpacityDesktop, 'desktop'),
    ].join(' ');
  }

  getDisplayClassFunction(
    obj: Partial<displayInterface>,
    displayType: AppTailwindDisplayType = 'block',
  ): string {
    // code to call:

    // this.classes.getDisplayClassFunction({
    //   display: this.display,
    //   displayTablet: this.displayTablet,
    //   displayDesktop: this.displayDesktop,
    // }),

    return [
      this.getDisplayClass(obj?.display, '', displayType),
      this.getDisplayClass(obj?.displayTablet, 'tablet', displayType),
      this.getDisplayClass(obj?.displayDesktop, 'desktop', displayType),
    ].join(' ');
  }

  getColXGapClassFunction(obj: Partial<colXGapInterface>): string {
    // code to call:

    // this.classes.getColXGapClassFunction({
    //   gapX: this.gapX,
    //   gapXTablet: this.gapXTablet,
    //   gapXDesktop: this.gapXDesktop,
    // }),

    return [
      this.getColXGapClass(obj?.gapX),
      this.getColXGapClass(obj?.gapXTablet, 'tablet'),
      this.getColXGapClass(obj?.gapXDesktop, 'desktop'),
    ].join(' ');
  }

  getColYGapClassFunction(obj: Partial<colYGapInterface>): string {
    // code to call:

    // this.classes.getColYGapClassFunction({
    //   gapY: this.gapY,
    //   gapYTablet: this.gapYTablet,
    //   gapYDesktop: this.gapYDesktop,
    // }),

    return [
      this.getColYGapClass(obj?.gapY),
      this.getColYGapClass(obj?.gapYTablet, 'tablet'),
      this.getColYGapClass(obj?.gapYDesktop, 'desktop'),
    ].join(' ');
  }

  getColSpanClassesFunction(obj: Partial<colSpanInterface>): string {
    // code to call:

    // this.classes.getColSpanClassesFunction({
    //   colSpan: this.colSpan,
    //   colSpanTablet: this.colSpanTablet,
    //   colSpanDesktop: this.colSpanDesktop,
    // }),

    return [
      this.getColSpanClass(obj?.colSpan),
      this.getColSpanClass(obj?.colSpanTablet, 'tablet'),
      this.getColSpanClass(obj?.colSpanDesktop, 'desktop'),
    ].join(' ');
  }

  getMarginClassesFunction(obj: Partial<marginInterface>): string {
    // code to call:

    // this.classes.getMarginClassesFunction({
    //   marginY: this.marginY,
    //   marginYTablet: this.marginYTablet,
    //   marginYDesktop: this.marginYDesktop,
    //   marginBottom: this.marginBottom,
    //   marginBottomTablet: this.marginBottomTablet,
    //   marginBottomDesktop: this.marginBottomDesktop,
    //   marginTop: this.marginTop,
    //   marginTopTablet: this.marginTopTablet,
    //   marginTopDesktop: this.marginTopDesktop,
    //   marginX: this.marginX,
    //   marginXTablet: this.marginXTablet,
    //   marginXDesktop: this.marginXDesktop,
    //   marginLeft: this.marginLeft,
    //   marginLeftTablet: this.marginLeftTablet,
    //   marginLeftDesktop: this.marginLeftDesktop,
    //   marginRight: this.marginRight,
    //   marginRightTablet: this.marginRightTablet,
    //   marginRightDesktop: this.marginRightDesktop,
    // }),

    return [
      this.getMarginYClass(obj?.marginY),
      this.getMarginYClass(obj?.marginYTablet, 'tablet'),
      this.getMarginYClass(obj?.marginYDesktop, 'desktop'),
      this.getMarginBottomClass(obj?.marginBottom),
      this.getMarginBottomClass(obj?.marginBottomTablet, 'tablet'),
      this.getMarginBottomClass(obj?.marginBottomDesktop, 'desktop'),
      this.getMarginTopClass(obj?.marginTop),
      this.getMarginTopClass(obj?.marginTopTablet, 'tablet'),
      this.getMarginTopClass(obj?.marginTopDesktop, 'desktop'),
      this.getMarginXClass(obj?.marginX),
      this.getMarginXClass(obj?.marginXTablet, 'tablet'),
      this.getMarginXClass(obj?.marginXDesktop, 'desktop'),
      this.getMarginLeftClass(obj?.marginLeft),
      this.getMarginLeftClass(obj?.marginLeftTablet, 'tablet'),
      this.getMarginLeftClass(obj?.marginLeftDesktop, 'desktop'),
      this.getMarginRightClass(obj?.marginRight),
      this.getMarginRightClass(obj?.marginRightTablet, 'tablet'),
      this.getMarginRightClass(obj?.marginRightDesktop, 'desktop'),
    ].join(' ');
  }

  getPaddingsClassesFunction(obj: Partial<paddingsInterface>): string {
    // code to call:

    // this.classes.getPaddingsClassesFunction({
    //   paddingY: this.paddingY,
    //   paddingYTablet: this.paddingYTablet,
    //   paddingYDesktop: this.paddingYDesktop,
    //   paddingBottom: this.paddingBottom,
    //   paddingBottomTablet: this.paddingBottomTablet,
    //   paddingBottomDesktop: this.paddingBottomDesktop,
    //   paddingTop: this.paddingTop,
    //   paddingTopTablet: this.paddingTopTablet,
    //   paddingTopDesktop: this.paddingTopDesktop,
    //   paddingX: this.paddingX,
    //   paddingXTablet: this.paddingXTablet,
    //   paddingXDesktop: this.paddingXDesktop,
    //   paddingLeft: this.paddingLeft,
    //   paddingLeftTablet: this.paddingLeftTablet,
    //   paddingLeftDesktop: this.paddingLeftDesktop,
    //   paddingRight: this.paddingRight,
    //   paddingRightTablet: this.paddingRightTablet,
    //   paddingRightDesktop: this.paddingRightDesktop,
    // }),

    return [
      this.getPaddingYClass(obj?.paddingY),
      this.getPaddingYClass(obj?.paddingYTablet, 'tablet'),
      this.getPaddingYClass(obj?.paddingYDesktop, 'desktop'),
      this.getPaddingBottomClass(obj?.paddingBottom),
      this.getPaddingBottomClass(obj?.paddingBottomTablet, 'tablet'),
      this.getPaddingBottomClass(obj?.paddingBottomDesktop, 'desktop'),
      this.getPaddingTopClass(obj?.paddingTop),
      this.getPaddingTopClass(obj?.paddingTopTablet, 'tablet'),
      this.getPaddingTopClass(obj?.paddingTopDesktop, 'desktop'),
      this.getPaddingXClass(obj?.paddingX),
      this.getPaddingXClass(obj?.paddingXTablet, 'tablet'),
      this.getPaddingXClass(obj?.paddingXDesktop, 'desktop'),
      this.getPaddingLeftClass(obj?.paddingLeft),
      this.getPaddingLeftClass(obj?.paddingLeftTablet, 'tablet'),
      this.getPaddingLeftClass(obj?.paddingLeftDesktop, 'desktop'),
      this.getPaddingRightClass(obj?.paddingRight),
      this.getPaddingRightClass(obj?.paddingRightTablet, 'tablet'),
      this.getPaddingRightClass(obj?.paddingRightDesktop, 'desktop'),
    ].join(' ');
  }

  getFlexClassesFunction(obj: Partial<flexInterface>): string {
    // code to call:

    // this.classes.getFlexClassesFunction({
    //   flexDirection: this.flexDirection,
    //   flexDirectionTablet: this.flexDirectionTablet,
    //   flexDirectionDesktop: this.flexDirectionDesktop,
    //   flexAlign: this.flexAlign,
    //   flexAlignTablet: this.flexAlignTablet,
    //   flexAlignDesktop: this.flexAlignDesktop,
    //   flexJustify: this.flexJustify,
    //   flexJustifyTablet: this.flexJustifyTablet,
    //   flexJustifyDesktop: this.flexJustifyDesktop,
    //   flexWrap: this.flexWrap,
    //   gap: this.gap,
    //   gapTablet: this.gapTablet,
    //   gapDesktop: this.gapDesktop,
    // }),
    return [
      this.getFlexDirectionClass(obj?.flexDirection),
      this.getFlexDirectionClass(obj?.flexDirectionTablet, 'tablet'),
      this.getFlexDirectionClass(obj?.flexDirectionDesktop, 'desktop'),
      this.getFlexAlignClass(obj?.flexAlign),
      this.getFlexAlignClass(obj?.flexAlignTablet, 'tablet'),
      this.getFlexAlignClass(obj?.flexAlignDesktop, 'desktop'),
      this.getFlexJustifyClass(obj?.flexJustify),
      this.getFlexJustifyClass(obj?.flexJustifyTablet, 'tablet'),
      this.getFlexJustifyClass(obj?.flexJustifyDesktop, 'desktop'),
      this.getFlexWrapClass(obj?.flexWrap),
      this.getGapClass(obj?.gap),
      this.getGapClass(obj?.gapTablet, 'tablet'),
      this.getGapClass(obj?.gapDesktop, 'desktop'),
    ].join(' ');
  }

  getGradientClassesFunction(obj: Partial<gradientInterface>): string {
    // code to call:

    // this.classes.getGradientClassesFunction({
    //   gradientDirection: this.gradientDirection,
    //   gradientDirectionTablet: this.gradientDirectionTablet,
    //   gradientDirectionDesktop: this.gradientDirectionDesktop,
    //   gradientColor: this.gradientColor,
    // }),
    return [
      this.getGradientDirectionClass(obj?.gradientDirection),
      this.getGradientDirectionClass(obj?.gradientDirectionTablet, 'tablet'),
      this.getGradientDirectionClass(obj?.gradientDirectionDesktop, 'desktop'),
      this.getGradientColorClass(obj?.gradientColor),
    ].join(' ');
  }

  getBackgroundPositionClassFunction(obj: Partial<bgPosInterface>): string {
    // code to call:

    // this.classes.getBackgroundPositionClassFunction({
    //   bgPosition: this.bgPosition,
    //   bgPositionTablet: this.bgPositionTablet,
    //   bgPositionDesktop: this.bgPositionDesktop,
    // }),
    return [
      this.getBackgroundPositionClass(obj?.bgPosition),
      this.getBackgroundPositionClass(obj?.bgPositionTablet, 'tablet'),
      this.getBackgroundPositionClass(obj?.bgPositionDesktop, 'desktop'),
    ].join(' ');
  }

  getBackgroundColorsFunction(obj: Partial<backgroundColorsInterface>): string {
    // code to call:

    // this.classes.getBackgroundColorsFunction({
    //   bgColor: this.bgColor,
    //   bgColorTablet: this.bgColorTablet,
    //   bgColorDesktop: this.bgColorDesktop,
    // }),
    return [
      this.getBackgroundColorClass(obj?.bgColor),
      this.getBackgroundColorClass(obj?.bgColorTablet, 'tablet'),
      this.getBackgroundColorClass(obj?.bgColorDesktop, 'desktop'),
    ].join(' ');
  }

  getBackgroundShadesFunction(obj: Partial<bgColorsShadesInterface>): string {
    // code to call:

    // this.classes.getBackgroundShadesFunction({
    //   bgShadeColor: this.bgShadeColor,
    //   bgShadeColorTablet: this.bgShadeColorTablet,
    //   bgShadeColorDesktop: this.bgShadeColorDesktop,
    // }),
    return [
      this.getBackgroundColorShadesClass(obj?.bgShadeColor),
      this.getBackgroundColorShadesClass(obj?.bgShadeColorTablet, 'tablet'),
      this.getBackgroundColorShadesClass(obj?.bgShadeColorDesktop, 'desktop'),
    ].join(' ');
  }

  getBorderRadiusFunction(obj: Partial<borderRadiusInterface>): string {
    // code to call:

    // this.classes.getBorderRadiusFunction({
    //   borderRadius: this.borderRadius,
    //   borderRadiusTablet: this.borderRadiusTablet,
    //   borderRadiusDesktop: this.borderRadiusDesktop,
    // }),
    return [
      this.getBorderRadiusClass(obj?.borderRadius),
      this.getBorderRadiusClass(obj?.borderRadiusTablet, 'tablet'),
      this.getBorderRadiusClass(obj?.borderRadiusDesktop, 'desktop'),
    ].join(' ');
  }

  getTextClassesFunction(obj: Partial<textInterface>): string {
    // code to call:

    // this.classes.getTextClassesFunction({
    //   letterSpacing: this.letterSpacing,
    //   letterSpacingTablet: this.letterSpacingTablet,
    //   letterSpacingDesktop: this.letterSpacingDesktop,
    //   fontWeight: this.fontWeight,
    //   fontWeightTablet: this.fontWeightTablet,
    //   fontWeightDesktop: this.fontWeightDesktop,
    //   fontSize: this.fontSize,
    //   fontSizeTablet: this.fontSizeTablet,
    //   fontSizeDesktop: this.fontSizeDesktop,
    //   textAlign: this.textAlign,
    //   textAlignTablet: this.textAlignTablet,
    //   textAlignDesktop: this.textAlignDesktop,
    //   color: this.color,
    //   colorTablet: this.colorTablet,
    //   colorDesktop: this.colorDesktop,
    //   italic: this.italic,
    //   italicTablet: this.italicTablet,
    //   italicDesktop: this.italicDesktop,
    //   textTransform: this.textTransform,
    //   textTransformTablet: this.textTransformTablet,
    //   textTransformDesktop: this.textTransformDesktop,
    // }),

    return [
      this.getTextSpacingClass(obj?.letterSpacing),
      this.getTextSpacingClass(obj?.letterSpacingTablet, 'tablet'),
      this.getTextSpacingClass(obj?.letterSpacingDesktop, 'desktop'),
      this.getFontWeightClass(obj?.fontWeight),
      this.getFontWeightClass(obj?.fontWeightTablet, 'tablet'),
      this.getFontWeightClass(obj?.fontWeightDesktop, 'desktop'),
      this.getFontSizeClass(obj?.fontSize),
      this.getFontSizeClass(obj?.fontSizeTablet, 'tablet'),
      this.getFontSizeClass(obj?.fontSizeDesktop, 'desktop'),
      this.getTextAlignClass(obj?.textAlign),
      this.getTextAlignClass(obj?.textAlignTablet, 'tablet'),
      this.getTextAlignClass(obj?.textAlignDesktop, 'desktop'),
      this.getTextColorClass(obj?.color),
      this.getTextColorClass(obj?.colorTablet, 'tablet'),
      this.getTextColorClass(obj?.colorDesktop, 'desktop'),
      this.getItalicClass(obj?.italic),
      this.getItalicClass(obj?.italicTablet, 'tablet'),
      this.getItalicClass(obj?.italicDesktop, 'desktop'),
      this.getTextTransformClass(obj?.textTransform),
      this.getTextTransformClass(obj?.textTransformTablet, 'tablet'),
      this.getTextTransformClass(obj?.textTransformDesktop, 'desktop'),
    ].join(' ');
  }

  getMarginAlignClassesFunction(obj: Partial<marginAlignInterface>): string {
    // code to call:

    // this.classes.getMarginAlignClassesFunction({
    //   marginAlign: this.align,
    //   marginAlignTablet: this.alignTablet,
    //   marginAlignDesktop: this.alignDesktop,
    // }),

    return [
      this.getMarginAlignClass(obj?.marginAlign),
      this.getMarginAlignClass(obj?.marginAlignTablet, 'tablet'),
      this.getMarginAlignClass(obj?.marginAlignDesktop, 'desktop'),
    ].join(' ');
  }

  getImageMaxWidthClassesFunction(obj: Partial<imgMaxWInterface>): string {
    // code to call:

    // this.classes.getImageMaxWidthClassesFunction({
    //   imageWidth: this.fixedWidth,
    //   imageWidthTablet: this.fixedWidthTablet,
    //   imageWidthDesktop: this.fixedWidthDesktop,
    // }),

    return [
      this.getImageMaxWidthClass(obj?.imageWidth),
      this.getImageMaxWidthClass(obj?.imageWidthTablet, 'tablet'),
      this.getImageMaxWidthClass(obj?.imageWidthDesktop, 'desktop'),
    ].join(' ');
  }

  getWidthClassesFunction(obj: Partial<widthInterface>): string {
    // code to call:

    // this.classes.getWidthClassesFunction({
    //   width: this.width,
    //   widthTablet: this.widthTablet,
    //   widthDesktop: this.widthDesktop,
    // }),
    return [
      this.getWidthClass(obj?.width),
      this.getWidthClass(obj?.widthTablet, 'tablet'),
      this.getWidthClass(obj?.widthDesktop, 'desktop'),
    ].join(' ');
  }

  getMaxWidthClassesFunction(obj: Partial<maxWInterface>): string {
    // code to call:

    // this.classes.getMaxWidthClassesFunction({
    //   maxWidth: this.maxWidth,
    //   maxWidthTablet: this.maxWidthTablet,
    //   maxWidthDesktop: this.maxWidthDesktop,
    // }),

    return [
      this.getMaxWidthClass(obj?.maxWidth),
      this.getMaxWidthClass(obj?.maxWidthTablet, 'tablet'),
      this.getMaxWidthClass(obj?.maxWidthDesktop, 'desktop'),
    ].join(' ');
  }

  getHeightClassesFunction(obj: Partial<heightInterface>): string {
    // code to call:

    // this.classes.getHeightClassesFunction({
    //   height: this.height,
    //   heightTablet: this.heightTablet,
    //   heightDesktop: this.heightDesktop,
    // }),

    return [
      this.getHeightClass(obj?.height),
      this.getHeightClass(obj?.heightTablet, 'tablet'),
      this.getHeightClass(obj?.heightDesktop, 'desktop'),
    ].join(' ');
  }

  getBoxShadowClassFunction(obj: Partial<shadowInterface>): string {
    // code to call:

    // this.classes.getBoxShadowClassFunction({
    //   shadow: this.shadow,
    //   shadowTablet: this.shadowTablet,
    //   shadowDesktop: this.shadowDesktop,
    // }),

    return [
      this.getBoxShadowClass(obj?.shadow),
      this.getBoxShadowClass(obj?.shadowTablet, 'tablet'),
      this.getBoxShadowClass(obj?.shadowDesktop, 'desktop'),
    ].join(' ');
  }
}
